/**
 * useToggleChecklistItem — calls update-checklist-item Edge Function
 * to flip a single checklist row between (completed | not-applicable |
 * pending). Pattern mirrors useSubmitTask: structured Edge Function
 * errors become AppError.code so the UI can render an inline message.
 *
 * PROJECT_STATE task #72b.
 *
 * Optimistically updates the ['task', taskId] query so the row reflects
 * the new state immediately; rolls back on error. The server is the
 * authoritative state — on settle we invalidate to re-fetch.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import type { ChecklistItemRow, TaskBundle }                    from './useTask'

export interface ToggleVars {
  taskId:                 string
  itemId:                 string
  /** Pass exactly one of `completed` / `isNotApplicable`. */
  completed?:             boolean
  isNotApplicable?:       boolean
  notApplicableReason?:   string | null
}

export interface ToggleResult {
  item_id:                 string
  completed:               boolean
  is_not_applicable:       boolean
  not_applicable_reason:   string | null
  state_changed:           boolean
}

export function useToggleChecklistItem(): UseMutationResult<ToggleResult, AppError, ToggleVars, ToggleSnapshot> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<ToggleResult, AppError, ToggleVars, ToggleSnapshot>({
    mutationFn: async (vars): Promise<ToggleResult> => {
      const { data, error } = await supabase.functions.invoke<ToggleResult>('update-checklist-item', {
        body: {
          item_id:                vars.itemId,
          completed:              vars.completed,
          is_not_applicable:      vars.isNotApplicable,
          not_applicable_reason:  vars.notApplicableReason ?? null,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'TOGGLE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    // Optimistic update — the row snaps to the new state on tap, so the UI
    // feels instant on a cleaner's phone over flaky signal. The server's
    // authoritative state lands via the invalidate on settle below.
    onMutate: async (vars): Promise<ToggleSnapshot> => {
      const key = ['task', vars.taskId] as const
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<TaskBundle>(key)
      if (previous) {
        const nextItems: ChecklistItemRow[] = previous.items.map((item) => {
          if (item.id !== vars.itemId) return item
          // Mirror the server-side mutual-exclusivity rule: setting one
          // flag to true clears the other.
          let nextCompleted   = item.completed
          let nextIsNa        = item.is_not_applicable
          let nextReason      = item.not_applicable_reason
          if (typeof vars.completed === 'boolean') {
            nextCompleted = vars.completed
            if (vars.completed) { nextIsNa = false; nextReason = null }
          }
          if (typeof vars.isNotApplicable === 'boolean') {
            nextIsNa = vars.isNotApplicable
            if (vars.isNotApplicable) {
              nextCompleted = false
              nextReason    = vars.notApplicableReason ?? null
            } else {
              nextReason = null
            }
          }
          return {
            ...item,
            completed:             nextCompleted,
            is_not_applicable:     nextIsNa,
            not_applicable_reason: nextReason,
          }
        })
        queryClient.setQueryData<TaskBundle>(key, { ...previous, items: nextItems })
      }
      return { previous }
    },

    onError: (_err, vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(['task', vars.taskId], ctx.previous)
      }
    },

    onSettled: (_data, _err, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task', vars.taskId] })
    },
  })
}

interface ToggleSnapshot {
  previous: TaskBundle | undefined
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'NO_FLAG_PROVIDED':        return 'Nothing to update — pass a completed or N/A change.'
    case 'MUTUALLY_EXCLUSIVE_FLAGS':return 'Pick one — either mark it complete OR not applicable, not both.'
    case 'REASON_TOO_LONG':         return 'Reason must be 200 characters or fewer.'
    case 'INVALID_STATE':           return "This task is already submitted — items can't be changed."
    case 'ITEM_NOT_FOUND':          return 'This checklist item could not be found.'
    case 'FORBIDDEN_ROLE':          return 'Your role is not allowed to change this item.'
    case 'FORBIDDEN_OWNERSHIP':     return 'You can only edit items on tasks assigned to you.'
    case 'FORBIDDEN_ORG':           return 'This item does not belong to your organization.'
    case 'RATE_LIMITED':            return 'Too many changes in a row. Pause for a moment.'
    default:                        return detail || 'Could not update the item.'
  }
}
